import { globalEvents } from "@/data/Data";
import { dataEvents } from "@/data/events/dataEvents";
import { PaymentFor } from "@/data/model/Enums";
import type { IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";
import type { ISalesReportData } from "@/data/model/SalesReportData";
import type { ShowRoomPlanEventData } from "@/data/model/Table";
import { CurrentAppService } from "@/data/services/CurrentAppService";
import { CurrentIdentityService } from "@/data/services/CurrentIdentityService";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { Logging } from "@/data/util/Logging";



import { windowHistoryService } from "@/app/services/windowHistoryService";



import { Operator } from "@/operator/context/Operator";
import { ViewTransition } from "@/operator/controller/BaseController";
import { ItemsController } from "@/operator/controller/ItemsController";
import { LoginController } from "@/operator/controller/LoginController";
import { OpenBillsController } from "@/operator/controller/OpenBillsController";
import { OrdersController } from "@/operator/controller/OrdersController";
import { PaymentMethodsReportController } from "@/operator/controller/PaymentMethodsReportController";
import { PaymentsController } from "@/operator/controller/PaymentsController";
import { ProductionController } from "@/operator/controller/ProductionController";
import { RoomPlanController } from "@/operator/controller/RoomPlanController";
import { SalesReportController } from "@/operator/controller/SalesReportController";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { SalesReportsOptionsModal } from "@/operator/modal/SalesReportsOptionsModal";
import { SettingsModal } from "@/operator/modal/SettingsModal";
import { SessionExpiredPopup } from "@/operator/popups/SessionExpiredPopup";
import { OperatorCurrentIdentityService } from "@/operator/services/OperatorCurrentIdentityService";
import { OperatorCurrentLocationService, OperatorCurrentService } from "@/operator/services/OperatorCurrentLocationService";
import { Interactor } from "@/operator/ui/Interactor";


export type OperatorClientConfig = {
    callbackParams?: ITransactionCallbackParams;
};

export interface ITransactionCallbackParams {
    tableId: string;
    billId: string | undefined;
    walleePostfinanceTransactionId: string | undefined;
    externalPaymentId: string | undefined;
}

export class OperatorClient {
    private interactor: Interactor;

    private loginController?: LoginController;
    private roomPlanController?: RoomPlanController;
    private productionController?: ProductionController;
    private salesReportController?: SalesReportController;
    private paymentMethodsReportController?: PaymentMethodsReportController;
    private itemsController?: ItemsController;
    private openBillsController?: OpenBillsController;
    private ordersController?: OrdersController;
    private settingsModal?: SettingsModal;
    private paymentsController?: PaymentsController;

    private $mainContainer: JQuery;

    public static context: Operator;

    constructor($mc: JQuery, config?: OperatorClientConfig) {
        CurrentLocationService.init(new OperatorCurrentLocationService());
        CurrentIdentityService.init(new OperatorCurrentIdentityService());
        CurrentAppService.init(new OperatorCurrentService());

        this.interactor = new Interactor();

        if (OperatorClient.context === undefined) {
            OperatorClient.context = new Operator();
        }

        operatorEvents.context.authenticationChanged.add((isAuthenticated) => {
            if (isAuthenticated) {
                if (OperatorClient.context.hasMissingSettings()) this.showSettings();

                this.showProduction();
            } else this.showLogin();
        }, this);

        operatorEvents.context.locationChanged.add(() => {
            this.updatePageTitle();
        });

        operatorEvents.context.settingsChanged.add(() => {
            this.updatePageTitle();
        });

        this.$mainContainer = $mc;

        operatorEvents.views.ShowProduction.add(() => this.showProduction());
        operatorEvents.views.ShowRoomPlan.add((eventData) => this.showRoomPlan(eventData));
        operatorEvents.views.ShowSettings.add(() => this.showSettings());
        operatorEvents.views.ShowReports.add(() => this.showReportsPopup());
        operatorEvents.views.ShowItems.add(() => this.showItems());
        operatorEvents.views.ShowOpenBills.add(() => this.showOpenBills());
        operatorEvents.views.ShowOrders.add(() => this.showOrders());
        operatorEvents.views.ShowPayments.add(() => this.showPayments());

        document.body.addEventListener(
            "touchmove",
            (e) => {
                const target = $(e.target as any);
                if (target.parents(".scrollable").length === 0) {
                    e.preventDefault();
                    return;
                }

                if (target.parents(".dragable").length !== 0) {
                    // if scrollable is closer to the element, then skip this check
                    if (target.parentsUntil(".dragable").length < target.parentsUntil(".scrollable").length) {
                        e.preventDefault();
                        return;
                    }
                }
            },
            { passive: false }
        );

        this.interactor.onReactivate = () => {
            Logging.LogSocketNotification(`On reactivate`);
            OperatorClient.context.reactivateSocket();
        };

        this.interactor.onCameBackAfterLongTime = () => {
            Logging.LogSocketNotification(`On came back after long time`);
            location.reload();
        };

        globalEvents.OnSessionExpired.add(() => this.showSessionExpiredPopup());
        dataEvents.operatorSocket.UserSessionExpired.add(() => this.showSessionExpiredPopup(true));

        this.init(config);
    }

    private showLogin() {
        if (this.loginController === undefined) {
            this.loginController = new LoginController();
            this.loginController.stackTo(this.$mainContainer);
        }

        this.loginController.show();
    }

    private showRoomPlan(options: ShowRoomPlanEventData) {
        if (this.roomPlanController === undefined) {
            this.roomPlanController = new RoomPlanController();
            this.roomPlanController.stackTo(this.$mainContainer);
        }

        this.roomPlanController.show(ViewTransition.Fade, options);
    }

    private showItems() {
        if (this.itemsController === undefined) {
            this.itemsController = new ItemsController();
            this.itemsController.stackTo(this.$mainContainer);
        }

        this.itemsController.showView(ViewTransition.Fade);
    }

    private showOpenBills() {
        if (this.openBillsController === undefined) {
            this.openBillsController = new OpenBillsController();
            this.openBillsController.stackTo(this.$mainContainer);
        }

        this.openBillsController.show(ViewTransition.Fade);
    }

    private showOrders() {
        if (this.ordersController === undefined) {
            this.ordersController = new OrdersController();
            this.ordersController.stackTo(this.$mainContainer);
        }

        this.ordersController.showView(ViewTransition.Fade);
    }

    private showPayments() {
        if (this.paymentsController === undefined) {
            this.paymentsController = new PaymentsController();
            this.paymentsController.stackTo(this.$mainContainer);
        }

        this.paymentsController.showView(ViewTransition.Fade);
    }

    private showSalesReport(data: ISalesReportData) {
        if (this.salesReportController === undefined) {
            this.salesReportController = new SalesReportController();
            this.salesReportController.stackTo(this.$mainContainer);
        }

        this.salesReportController.showView(data, ViewTransition.Fade);
    }

    private showPaymentMethodsReport(data: IPaymentMethodsReportData) {
        if (this.paymentMethodsReportController === undefined) {
            this.paymentMethodsReportController = new PaymentMethodsReportController();
            this.paymentMethodsReportController.stackTo(this.$mainContainer);
        }

        this.paymentMethodsReportController.showView(data, ViewTransition.Fade);
    }

    private showSettings() {
        if (this.settingsModal === undefined) this.settingsModal = new SettingsModal();

        this.settingsModal.open();
    }

    private showReportsPopup() {
        new SalesReportsOptionsModal(
            (data) => {
                this.showSalesReport(data);
            },
            (data) => {
                this.showPaymentMethodsReport(data);
            }
        ).show();
    }

    private showProduction(transition?: ViewTransition) {
        if (this.productionController === undefined) {
            this.productionController = new ProductionController();
            this.productionController.stackTo(this.$mainContainer);
        }

        this.productionController.show(transition);
    }

    private showSessionExpiredPopup(isAccessRightsChange: boolean = false) {
        if (!OperatorClient.context.isAuthenticated()) return;

        SessionExpiredPopup.show({
            isAccessRightsChange: isAccessRightsChange,
            onConfirm: () => this.showLogin(),
        });
    }

    private async init(config?: OperatorClientConfig) {
        await OperatorClient.context.init();

        if (!OperatorClient.context.isAuthenticated()) {
            this.showLogin();
            return;
        }

        if (OperatorClient.context.hasMissingSettings()) {
            this.showSettings();
            return;
        }

        if (config?.callbackParams != null) {
            windowHistoryService.pushState({ tableId: config.callbackParams.tableId }, "/")
            operatorEvents.showOrderAndPayView.dispatch({
                force: true,
                tableId: config.callbackParams.tableId,
                billId: config.callbackParams.billId,
                externalPaymentId: config.callbackParams.externalPaymentId,
                transactionId: config.callbackParams.walleePostfinanceTransactionId,
                transactionFor: config.callbackParams.billId == null || config.callbackParams.billId.length == 0 ? PaymentFor.Order : PaymentFor.Bill,
            });
        } else {
            this.showProduction();
        }
    }

    private updatePageTitle() {
        const locationName = OperatorClient.context?.settings?.location?.name;
        const title = locationName ? `${locationName} - ${trans.app.title}` : trans.app.title;
        document.querySelector("title")!.innerHTML = title;
    }
}
